import React from 'react'
import { IonContent, IonPage, useIonViewDidEnter, useIonViewWillEnter } from '@ionic/react'
import HeaderNav from '../../../components/headerNav/HeaderNav.component'
import { useDispatch, useSelector } from 'react-redux'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'
import PatientInformationMobile from '../patientInformationContentMobile/PatientInformationMobile.component'
import PatientInformationTablet from '../patientInformationContentTablet/PatientInformationTablet.component'
import { useTranslation } from 'react-i18next'
import { setIsLoading } from '../../patientAllergies/store/patientAllergies.actions'
import { useHistory } from 'react-router-dom'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import './PatientInformation.page.css'
import { getPatientMedicationsOptions } from '../../options/store/options.actions'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import { setSelectedOption } from '../store/patientInformation.actions'
import { PatientInformationConstants } from '../store/patientInformation.constants'
import { getPatientInformationState } from '../selectors'
import { useOfflineStatus } from '../../../utilities/isOnline.util'
import { newPtClick } from '../../../utilities/patientSearch.util'
import { IPatientSearchState } from '../../patientSearch/store/patientSearch.types'
import { getPatientSearchState } from '../../patientSearch/selectors'
import { getPatientDemographicsSelector } from '../../patientDemographics/selectors'
import { IPatientDemographicsState } from '../../patientDemographics/store/patientDemographics.types'
import { hasUnsavedPhysiciansData, showUnsavedDataWarningMessage } from '../../../utilities/unSavedData.util'
import {
  clearPhysicianData,
  savePatientDemographics,
  setPatientDemographicsDefaults,
} from '../../patientDemographics/store/patientDemographics.actions'
import { clearValidationErrorsByPathList } from '../../../utilities/runRulesValidation.util'
import { PatientDemographicsConstants } from '../../patientDemographics/store/patientDemographics.constants'
import { getRulesSelector } from '../../rules/selectors'
import { IRulesState } from '../../rules/store/rules.types'

const PatientInformation: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { hidePatientAndBilling } = useSelector(getPatientInformationState)
  const isOfflineStatus = useOfflineStatus()
  const patientSearchState: IPatientSearchState = useSelector(getPatientSearchState)
  const { showImportDataConfirmationAlert } = patientSearchState
  const patientDemographics: IPatientDemographicsState = useSelector(getPatientDemographicsSelector)
  const rules: IRulesState = useSelector(getRulesSelector)
  const { isTablet } = useDeviceMetrics()

  useIonViewWillEnter(() => {
    const content: HTMLIonContentElement | null = document.getElementById('main-content') as HTMLIonContentElement
    content?.scrollToTop(0)
    dispatch(setIsLoading(false))
    dispatch(getPatientMedicationsOptions())
    return
  })

  useIonViewDidEnter(() => {
    if (!patientDemographics.__areDefaultsSet) {
      dispatch(setPatientDemographicsDefaults(true))
    }
  }, [patientDemographics.__areDefaultsSet])

  const onBackClickYes = () => {
    dispatch(clearPhysicianData())
    dispatch(savePatientDemographics())
    rules.errors = clearValidationErrorsByPathList(rules.errors, [
      PatientDemographicsConstants.SET_PHYSICIAN_LASTNAME,
      PatientDemographicsConstants.SET_PHYSICIAN_FIRSTNAME,
      PatientDemographicsConstants.SET_PHYSICIAN_MIDDLENAME,
    ])
    if (isTablet && hidePatientAndBilling) {
      dispatch(setSelectedOption(PatientInformationConstants.PATIENT_SEARCH_OPTION))
    } else {
      dispatch(setSelectedOption(PatientInformationConstants.PATIENT_DEMOGRAPHICS_OPTION))
    }
    history.push(TabConstants.CHART_ROUTE)
  }

  const onBackClick = () => {
    if (hasUnsavedPhysiciansData(patientDemographics)) {
      showUnsavedDataWarningMessage(dispatch, t, t('patientDemographics.unsavedDataWarningMessage'), onBackClickYes)
      return
    }
    onBackClickYes()
  }

  const onNewPtClick = () => {
    newPtClick(dispatch, showImportDataConfirmationAlert, t, patientSearchState, history)
  }

  return (
    <IonPage id='PatientInformationPage'>
      <PCRHeader />
      <HeaderNav
        prevOnClick={onBackClick}
        prevLabel={t('common.buttons.Back')}
        title='Patient Information'
        isBackButton={true}
        nextLabel={isOfflineStatus && !isTablet ? t('patientSearch.newPatient') : undefined}
        nextOnClick={onNewPtClick}
      />
      <IonContent color='light'>
        {isTablet && <PatientInformationTablet />}
        {!isTablet && <PatientInformationMobile />}
      </IonContent>
    </IonPage>
  )
}

export default PatientInformation
